/* Banking Portal · Tier-2/3 Program — Waves 16/17/18 (Feb 2026).
   Three pages in one file:
     Wave-16 · /banking/behavioral
     Wave-17 · /banking/cyber
     Wave-18 · /banking/ops-defense
*/
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, CheckCircle2, XCircle, Eye, Lock, Layers,
  Building2, ShieldCheck, ShieldAlert, Network, FileCheck2, Filter,
  ChevronRight, TrendingUp, ClipboardList, Bug, KeyRound, FileSearch,
  Globe, Clock, Users, AlertOctagon, BadgeCheck, Hash, Mail, Briefcase,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function money(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function dateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function ago(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const d = Math.floor(ms / 86400000);
  if (d < 1) return "today";
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const cls = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
    violet:  "bg-violet-50 border-violet-200",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const SEVERITY_CLS = {
  critical:    "bg-rose-50 text-rose-700 border-rose-200",
  high:        "bg-amber-50 text-amber-700 border-amber-200",
  medium:      "bg-sky-50 text-sky-700 border-sky-200",
};

// ═════════════════════════════════════════════════════════════════════════
// Wave-16 · BEHAVIORAL BASELINES & FIRST-SEEN
// ═════════════════════════════════════════════════════════════════════════
function AnomaliesTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/behavioral/anomalies?limit=50")
      .then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="anomalies-tab">
      <p className="text-[12.5px] text-slate-500">
        Customers showing recent transactions exceeding their 180-day baseline by 2σ. Tap a row to inspect.
      </p>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Customer</th>
              <th className="text-right px-3 py-2 font-medium">Recent max</th>
              <th className="text-right px-3 py-2 font-medium">Baseline mean</th>
              <th className="text-right px-3 py-2 font-medium">Threshold (2σ)</th>
              <th className="text-right px-3 py-2 font-medium">Deviation</th>
              <th className="text-left px-3 py-2 font-medium">Drill</th>
            </tr>
          </thead>
          <tbody data-testid="anomaly-rows">
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No anomalies detected.</td></tr>
            )}
            {data.rows.map(r => (
              <tr key={r.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    <div>
                      <div className="text-[13px] text-slate-900">{r.company}</div>
                      <div className="text-[10.5px] text-slate-500 font-mono">{r.email_masked} · {r.role}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium text-rose-600">{money(r.recent_max_usd)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{money(r.baseline_mean)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{money(r.threshold_usd)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-600">+{fmt(r.deviation_pct)}%</td>
                <td className="px-3 py-2.5">
                  <Link to={`/banking/customer-profile/${r.user_id}`} className="text-[11px] text-sky-600 hover:underline flex items-center">
                    Profile <ChevronRight className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10.5px] text-slate-500 italic">{data._methodology}</p>
    </div>
  );
}

function FirstSeenTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    api.get(`/partner/banking/behavioral/first-seen-alerts?days=${days}&min_amount=10000`)
      .then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="first-seen-tab">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-[11.5px] text-slate-500 tabular-nums">
          {fmt(data.count)} first-seen invoices · total {money(data.total_amount_usd)}
        </span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Buyer</th>
              <th className="text-left px-3 py-2 font-medium">Seller (first-seen)</th>
              <th className="text-right px-3 py-2 font-medium">Amount</th>
              <th className="text-left px-3 py-2 font-medium">When</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody data-testid="first-seen-rows">
            {data.rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No first-seen relationships above the threshold.</td></tr>
            )}
            {data.rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5">
                  <div className="text-[12.5px] text-slate-900">{r.buyer?.company || "—"}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono">{r.buyer?.email_masked || "—"}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-[12.5px] text-slate-900">{r.seller?.company || "—"}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono">{r.seller?.email_masked || "—"}</div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">{money(r.amount)}</td>
                <td className="px-3 py-2.5 text-[11.5px] text-slate-500">{dateOnly(r.created_at)}</td>
                <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10.5px] capitalize">{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BaselineSearchTab() {
  const [uid, setUid] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const r = await api.get(`/partner/banking/behavioral/baseline/${uid}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Customer not found" : "Could not load baseline");
      setData(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="baseline-search-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <Label className="text-xs">Customer user id</Label>
        <div className="flex gap-2 mt-1.5">
          <Input value={uid} onChange={e => setUid(e.target.value)} placeholder="user id…"
                 className="text-sm font-mono" data-testid="baseline-uid-input" />
          <Button onClick={load} disabled={!uid || busy} data-testid="baseline-go-btn">{busy ? "…" : "Show baseline"}</Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Tip: pick a customer from <Link to="/banking/customer-profile" className="text-sky-600">Customer Profile search</Link> to copy an id.
        </p>
      </div>

      {data && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" />{data.user.company}</h3>
              <Badge variant="outline" className="text-[10px] capitalize">{data.user.role}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Baseline txns" value={fmt(data.baseline.txn_count)} sub={`${data.baseline.window_days}d window`} />
              <Kpi label="Mean amount" value={money(data.baseline.amount_mean_usd)} sub={`±${money(data.baseline.amount_stdev_usd)}`} />
              <Kpi label="P50 / P90" value={`${money(data.baseline.amount_percentiles.p50)} / ${money(data.baseline.amount_percentiles.p90)}`} />
              <Kpi label="Recent anomalies" value={fmt(data.recent_7d.anomaly_count)}
                   tone={data.recent_7d.anomaly_count > 0 ? "rose" : "emerald"}
                   icon={AlertTriangle} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h4 className="text-sm font-semibold mb-3">Hour-of-day pattern (UTC)</h4>
              <div className="flex items-end gap-0.5 h-24">
                {Object.entries(data.baseline.hour_distribution_utc).map(([h, n]) => {
                  const max = Math.max(...Object.values(data.baseline.hour_distribution_utc), 1);
                  const pctHeight = Math.round((n / max) * 100);
                  const isTop = data.baseline.top_hours_utc.includes(h);
                  return (
                    <div key={h} className="flex-1 flex flex-col items-center justify-end">
                      <div className={`w-full ${isTop ? "bg-sky-400" : "bg-slate-200"}`} style={{ height: `${pctHeight}%` }} title={`${h}:00 · ${n} txns`} />
                      <div className="text-[8px] text-slate-400 mt-0.5">{h}</div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10.5px] text-slate-500 mt-2">Top hours UTC: {data.baseline.top_hours_utc.join(", ") || "—"}</p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h4 className="text-sm font-semibold mb-3">Top counterparties</h4>
              {data.baseline.top_counterparties.length === 0 ? (
                <div className="text-[11.5px] text-slate-400 italic">No invoice history.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.baseline.top_counterparties.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 last:border-0">
                        <td className="py-1.5 text-[12.5px] text-slate-900">{c.name}</td>
                        <td className="py-1.5 text-right text-[11.5px] tabular-nums text-slate-500">{fmt(c.count)} inv</td>
                        <td className="py-1.5 text-right text-[12.5px] tabular-nums text-slate-700">{money(c.vol_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {data.recent_7d.anomalies.length > 0 && (
            <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-4">
              <h4 className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Recent anomalies
              </h4>
              {data.recent_7d.anomalies.map((a, i) => (
                <div key={i} className="text-[11.5px] text-rose-800">
                  {a.kind === "amount_above_2sigma" && (
                    <>Amount ${a.amount.toLocaleString()} exceeded 2σ threshold ${a.threshold.toLocaleString()} (+{a.deviation_pct}% over mean)</>
                  )}
                  {a.kind === "off_hours_activity" && (
                    <>Off-hours activity at {a.hour}:00 UTC — baseline top hours: {a.baseline_top_hours.join(", ")}</>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-[10.5px] text-slate-500 italic">{data._methodology}</p>
        </div>
      )}
    </div>
  );
}

export function BankingBehavioral() {
  return (
    <div className="space-y-5" data-testid="banking-behavioral">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-500" /> Behavioral Baselines & First-Seen
        </h1>
        <p className="text-[13px] text-slate-500 mt-1">
          Catch anomalies BEFORE money moves. Per-customer baseline (180d), first-seen-beneficiary alerts ≥ $10K, and a portfolio-wide deviation roster.
        </p>
      </div>
      <Tabs defaultValue="anomalies" className="w-full">
        <TabsList data-testid="behavioral-tabs">
          <TabsTrigger value="anomalies"   data-testid="tab-anomalies">Portfolio Anomalies</TabsTrigger>
          <TabsTrigger value="first-seen"  data-testid="tab-first-seen">First-Seen ≥ $10K</TabsTrigger>
          <TabsTrigger value="baseline"    data-testid="tab-baseline">Per-Customer Baseline</TabsTrigger>
        </TabsList>
        <TabsContent value="anomalies"  className="mt-4"><AnomaliesTab /></TabsContent>
        <TabsContent value="first-seen" className="mt-4"><FirstSeenTab /></TabsContent>
        <TabsContent value="baseline"   className="mt-4"><BaselineSearchTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-17 · CYBER DEEP-DIVE
// ═════════════════════════════════════════════════════════════════════════
function CyberSbomTab() {
  const [data, setData] = useState(null);
  const [source, setSource] = useState("");

  useEffect(() => {
    api.get(`/partner/banking/cyber/sbom?limit=500${source ? `&source=${source}` : ""}`)
      .then(r => setData(r.data));
  }, [source]);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="sbom-tab">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <Select value={source || "all"} onValueChange={v => setSource(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="python">Python</SelectItem>
            <SelectItem value="javascript">JavaScript</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-[11.5px] text-slate-500 tabular-nums">
          {fmt(data.count)} deps · {fmt(data.flagged_count)} flagged
        </span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto max-h-[640px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Package</th>
              <th className="text-left px-3 py-2 font-medium">Version</th>
              <th className="text-left px-3 py-2 font-medium">Source</th>
              <th className="text-left px-3 py-2 font-medium">Vuln flag</th>
            </tr>
          </thead>
          <tbody data-testid="sbom-rows">
            {data.rows.map((r, i) => (
              <tr key={`${r.name}-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1.5 text-[12.5px] text-slate-900 font-mono">{r.name}</td>
                <td className="px-3 py-1.5 text-[11.5px] text-slate-500 font-mono">{r.version}</td>
                <td className="px-3 py-1.5 text-[10.5px]"><Badge variant="outline" className="text-[10px] capitalize">{r.source.replace(".txt","").replace(".json","")}</Badge></td>
                <td className="px-3 py-1.5">
                  {r.vuln_flag ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" title={r.vuln_flag}>
                      <AlertTriangle className="w-3 h-3 mr-1 inline" /> flagged
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">clean</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10.5px] text-slate-500 italic">{data._disclosure}</p>
    </div>
  );
}

function CyberHeadersTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/cyber/security-headers").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="headers-tab">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Expected headers" value={fmt(data.expected_count)} icon={ClipboardList} />
        <Kpi label="Present" value={fmt(data.present_count)} tone="emerald" icon={CheckCircle2} />
        <Kpi label="Coverage" value={`${data.coverage_pct}%`} tone={data.coverage_pct >= 80 ? "emerald" : data.coverage_pct >= 50 ? "amber" : "rose"} icon={ShieldCheck} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Header</th>
              <th className="text-left px-3 py-2 font-medium">Purpose</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>
          <tbody data-testid="headers-rows">
            {data.rows.map(r => (
              <tr key={r.header} className="border-t border-slate-100">
                <td className="px-3 py-2.5 text-[12.5px] font-mono text-slate-900">{r.header}</td>
                <td className="px-3 py-2.5 text-[11.5px] text-slate-600">{r.purpose}</td>
                <td className="px-3 py-2.5">
                  {r.present
                    ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]"><CheckCircle2 className="w-3 h-3 mr-1 inline" />present</Badge>
                    : <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10.5px]"><XCircle className="w-3 h-3 mr-1 inline" />missing</Badge>}
                </td>
                <td className="px-3 py-2.5 text-[10.5px] font-mono text-slate-500 max-w-md truncate">{r.value || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10.5px] text-slate-500">Probed against <span className="font-mono">{data.target}</span> · fetched: {String(data.fetched)}</p>
    </div>
  );
}

function CyberTlsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/cyber/tls").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const c = data.cert;
  const tone = c.tone === "emerald" ? "bg-emerald-50 border-emerald-200" : c.tone === "amber" ? "bg-amber-50 border-amber-200" : c.tone === "rose" ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200";

  return (
    <div className={`rounded-xl border-2 ${tone} p-5`} data-testid="tls-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Lock className="w-4 h-4" /> TLS certificate · {c.host || "—"}</h3>
        {c.days_remaining != null && (
          <Badge variant="outline" className={`text-[10.5px] tabular-nums ${
            c.days_remaining >= 30 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : c.days_remaining >= 7 ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-rose-50 text-rose-700 border-rose-200"
          }`}>
            {c.days_remaining}d remaining
          </Badge>
        )}
      </div>
      {c.fetched ? (
        <dl className="grid grid-cols-2 gap-y-1.5 text-[12.5px]">
          <dt className="text-slate-500">Subject</dt><dd className="text-slate-900 font-mono">{JSON.stringify(c.subject)}</dd>
          <dt className="text-slate-500">Issuer</dt><dd className="text-slate-900 font-mono">{c.issuer?.organizationName || JSON.stringify(c.issuer)}</dd>
          <dt className="text-slate-500">Not before</dt><dd className="text-slate-900">{c.not_before}</dd>
          <dt className="text-slate-500">Not after</dt><dd className="text-slate-900">{c.not_after}</dd>
        </dl>
      ) : (
        <div className="text-[12.5px] text-slate-600">
          Live TLS probe not possible from this environment.
          {c.fetch_error && <div className="text-[11px] text-slate-500 mt-1 font-mono">{c.fetch_error}</div>}
        </div>
      )}
    </div>
  );
}

function CyberSecretsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/cyber/secrets-rotation").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  const renderEnv = (env, label) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-slate-400" /> {label}</h4>
        <Badge variant="outline" className="text-[10.5px]">{fmt(env.key_count)} keys</Badge>
      </div>
      <div className="text-[11.5px] text-slate-500 mb-2">
        Last touched: {env.last_modified ? `${ago(env.last_modified)} ago` : "—"}
        {env.rotation_age_days != null && (
          <Badge variant="outline" className={`ml-2 text-[10px] ${
            env.rotation_age_days <= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {env.rotation_age_days <= 90 ? "≤90d (best practice)" : ">90d (rotation due)"}
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {Object.entries(env.by_class).map(([k, v]) => (
          <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
        ))}
      </div>
      <details className="text-[11px] text-slate-600">
        <summary className="cursor-pointer hover:text-slate-900">Show key names ({env.keys.length})</summary>
        <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-[10.5px] text-slate-500 max-h-60 overflow-y-auto">
          {env.keys.map(k => <div key={k}>{k}</div>)}
        </div>
      </details>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="secrets-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderEnv(data.backend_env, "Backend .env")}
        {renderEnv(data.frontend_env, "Frontend .env")}
      </div>
      <p className="text-[10.5px] text-slate-500 italic">{data._disclosure}</p>
    </div>
  );
}

function CyberPentestTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/cyber/pen-test").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="pentest-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Pen-test history</h4>
          <Badge variant="outline" className={data.last_real_pentest ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]" : "bg-amber-50 text-amber-700 border-amber-200 text-[10.5px]"}>
            {data.last_real_pentest ? `Last: ${dateOnly(data.last_real_pentest)}` : "No completed pen-test on file"}
          </Badge>
        </div>
        {data.rows.length === 0 ? (
          <p className="text-[12.5px] text-slate-400 italic mt-3">No pen-test records yet.</p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {data.rows.map((r, i) => (
              <div key={i} className="text-[11.5px] text-slate-600 flex items-center justify-between border-b border-slate-100 py-1.5 last:border-0">
                <span>{r.title || r.doc_key} · v{r.version || "1"} · target {r.target_quarter || "—"}</span>
                <Badge variant="outline" className={r.available && !r.is_placeholder ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"}>
                  {r.available && !r.is_placeholder ? "real" : "placeholder"}
                </Badge>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10.5px] text-slate-500 italic mt-3">{data._disclosure}</p>
      </div>
    </div>
  );
}

function CyberFreshnessTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/cyber/dependency-freshness").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="freshness-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Total deps" value={fmt(data.total_deps)} icon={Layers} />
        <Kpi label="Overall freshness" value={`${data.freshness_pct}%`} tone={data.freshness_pct >= 80 ? "emerald" : data.freshness_pct >= 50 ? "amber" : "rose"} icon={TrendingUp} />
        <Kpi label="Python · JS" value={`${data.python_recent_pct}% · ${data.js_recent_pct}%`} icon={ClipboardList} />
      </div>
      <p className="text-[10.5px] text-slate-500 italic">{data._disclosure}</p>
    </div>
  );
}

export function BankingCyber() {
  const [overview, setOverview] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/cyber/overview").then(r => setOverview(r.data));
  }, []);

  return (
    <div className="space-y-5" data-testid="banking-cyber">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Bug className="w-5 h-5 text-slate-500" /> Cyber Deep-Dive
        </h1>
        <p className="text-[13px] text-slate-500 mt-1">
          SBOM · security headers · TLS · secrets-rotation · pen-test · dependency freshness. Closes SOC 2 CC.6 (logical access) and CC.7 (system operations) evidence requirements while the formal audit window runs.
        </p>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cyber-overview-kpis">
          <Kpi label="Total deps" value={fmt(overview.deps_total)} icon={Layers} />
          <Kpi label="Python deps" value={fmt(overview.deps_python)} icon={ClipboardList} />
          <Kpi label="JS deps" value={fmt(overview.deps_javascript)} icon={ClipboardList} />
          <Kpi label="Vuln-flagged" value={fmt(overview.known_vulnerable_matches)} tone={overview.known_vulnerable_matches > 0 ? "amber" : "emerald"} icon={AlertTriangle} />
        </div>
      )}

      {overview?.soc2_cc_evidence && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold mb-2">SOC 2 control evidence (pre-attestation)</h3>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
            {Object.entries(overview.soc2_cc_evidence).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{k.toUpperCase().replace(/_/g, " ")}</div>
                <div className="text-[11.5px] text-slate-700 mt-1">{v}</div>
              </div>
            ))}
          </dl>
        </div>
      )}

      <Tabs defaultValue="sbom" className="w-full">
        <TabsList data-testid="cyber-tabs" className="flex-wrap">
          <TabsTrigger value="sbom"      data-testid="tab-sbom">SBOM</TabsTrigger>
          <TabsTrigger value="headers"   data-testid="tab-headers">Security Headers</TabsTrigger>
          <TabsTrigger value="tls"       data-testid="tab-tls">TLS</TabsTrigger>
          <TabsTrigger value="secrets"   data-testid="tab-secrets">Secrets Rotation</TabsTrigger>
          <TabsTrigger value="pentest"   data-testid="tab-pentest">Pen-Test</TabsTrigger>
          <TabsTrigger value="freshness" data-testid="tab-freshness">Dep Freshness</TabsTrigger>
        </TabsList>
        <TabsContent value="sbom"      className="mt-4"><CyberSbomTab /></TabsContent>
        <TabsContent value="headers"   className="mt-4"><CyberHeadersTab /></TabsContent>
        <TabsContent value="tls"       className="mt-4"><CyberTlsTab /></TabsContent>
        <TabsContent value="secrets"   className="mt-4"><CyberSecretsTab /></TabsContent>
        <TabsContent value="pentest"   className="mt-4"><CyberPentestTab /></TabsContent>
        <TabsContent value="freshness" className="mt-4"><CyberFreshnessTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-18 · OPERATIONAL DEFENSE HUB
// ═════════════════════════════════════════════════════════════════════════
function InsiderThreatTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [threshold, setThreshold] = useState(50);

  useEffect(() => {
    api.get(`/partner/banking/ops-defense/insider-threat?days=${days}&threshold=${threshold}`).then(r => setData(r.data));
  }, [days, threshold]);

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="insider-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(threshold)} onValueChange={v => setThreshold(Number(v))}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="20">≥ 20 reveals/day</SelectItem>
            <SelectItem value="50">≥ 50 reveals/day</SelectItem>
            <SelectItem value="100">≥ 100 reveals/day</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-[11.5px] text-slate-500 tabular-nums">
          {fmt(data.anomaly_count)} flagged · {fmt(data.daily_total)} total in window
        </span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Day</th>
              <th className="text-left px-3 py-2 font-medium">Admin (hashed)</th>
              <th className="text-right px-3 py-2 font-medium">PII reveals</th>
              <th className="text-left px-3 py-2 font-medium">Severity</th>
            </tr>
          </thead>
          <tbody data-testid="insider-rows">
            {data.rows.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No insider-threat anomalies — all admin PII access within normal range.</td></tr>
            )}
            {data.rows.map((r, i) => (
              <tr key={`${r.actor_hash}-${r.day}-${i}`} className="border-t border-slate-100">
                <td className="px-3 py-2.5 text-[12.5px]">{r.day}</td>
                <td className="px-3 py-2.5 font-mono text-[11.5px] text-slate-700">{r.actor_hash}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-600 font-medium">{fmt(r.reveal_count)}</td>
                <td className="px-3 py-2.5"><Badge variant="outline" className={`${SEVERITY_CLS[r.severity]} text-[10.5px]`}>{r.severity}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10.5px] text-slate-500 italic">{data._methodology}</p>
    </div>
  );
}

function ReconVarianceTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/ops-defense/recon-variance?days=30").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="recon-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total days" value={fmt(data.total_days)} icon={Clock} />
        <Kpi label="Flagged" value={fmt(data.flagged_days)} tone={data.flagged_days > 0 ? "amber" : "emerald"} icon={AlertTriangle} />
        <Kpi label="Mean daily net" value={money(data.mean_net_usd)} icon={TrendingUp} />
        <Kpi label="2σ threshold" value={money(data.threshold_usd)} icon={ShieldAlert} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Day</th>
              <th className="text-right px-3 py-2 font-medium">In</th>
              <th className="text-right px-3 py-2 font-medium">Out</th>
              <th className="text-right px-3 py-2 font-medium">Net</th>
              <th className="text-right px-3 py-2 font-medium">Cumulative</th>
              <th className="text-right px-3 py-2 font-medium">Txns</th>
              <th className="text-left px-3 py-2 font-medium">Flag</th>
            </tr>
          </thead>
          <tbody data-testid="recon-rows">
            {data.rows.map(r => (
              <tr key={r.day} className={`border-t border-slate-100 ${r.variance_flag ? "bg-rose-50/40" : ""}`}>
                <td className="px-3 py-2 text-[12px] font-mono">{r.day}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{money(r.in_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700">{money(r.out_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{money(r.net_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money(r.cumulative_net_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmt(r.txn_count)}</td>
                <td className="px-3 py-2">
                  {r.variance_flag && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">2σ+</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendorSlaTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/ops-defense/vendor-sla").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="vendor-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Vendors" value={fmt(data.vendor_count)} icon={Briefcase} />
        <Kpi label="Healthy" value={fmt(data.healthy)} tone="emerald" icon={CheckCircle2} />
        <Kpi label="Degraded" value={fmt(data.degraded)} tone="amber" icon={AlertTriangle} />
        <Kpi label="Broken" value={fmt(data.broken)} tone={data.broken > 0 ? "rose" : "emerald"} icon={XCircle} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Vendor</th>
              <th className="text-left px-3 py-2 font-medium">Category</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">SLA target</th>
              <th className="text-left px-3 py-2 font-medium">30d uptime</th>
              <th className="text-left px-3 py-2 font-medium">Last incident</th>
            </tr>
          </thead>
          <tbody data-testid="vendor-rows">
            {data.rows.map((v, i) => (
              <tr key={`${v.name}-${i}`} className="border-t border-slate-100">
                <td className="px-3 py-2.5 text-[12.5px] text-slate-900">{v.name}</td>
                <td className="px-3 py-2.5 text-[11.5px] text-slate-500">{v.category || "—"}</td>
                <td className="px-3 py-2.5">
                  <Badge variant="outline" className={
                    v.tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]"
                    : v.tone === "rose" ? "bg-rose-50 text-rose-700 border-rose-200 text-[10.5px]"
                    : "bg-amber-50 text-amber-700 border-amber-200 text-[10.5px]"
                  }>{v.status}</Badge>
                </td>
                <td className="px-3 py-2.5 text-[11px] text-slate-600">{v.sla_target}</td>
                <td className="px-3 py-2.5 text-[11.5px] tabular-nums">{v.uptime_30d}</td>
                <td className="px-3 py-2.5 text-[11px] text-slate-500">{v.last_incident ? ago(v.last_incident) + " ago" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AtoTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    api.get(`/partner/banking/ops-defense/ato-dashboard?days=${days}`).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="ato-tab">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Login attempts" value={fmt(data.total_attempts)} icon={Activity} />
        <Kpi label="Failures" value={fmt(data.total_failures)} tone={data.total_failures > 0 ? "amber" : "emerald"} icon={AlertTriangle} />
        <Kpi label="Failure rate" value={`${data.failure_rate_pct}%`} tone={data.failure_rate_pct >= 5 ? "rose" : data.failure_rate_pct >= 1 ? "amber" : "emerald"} icon={TrendingUp} />
        <Kpi label="Distinct IPs" value={fmt(data.distinct_ips_with_failures)} icon={Globe} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-amber-500" /> Credential stuffing</h4>
          <p className="text-[10.5px] text-slate-500 mb-2">One IP hitting 5+ distinct users</p>
          {data.cred_stuffing.length === 0 ? (
            <div className="text-[11.5px] text-slate-400 italic">None detected.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10.5px] uppercase text-slate-500"><tr>
                <th className="text-left py-1.5">IP</th>
                <th className="text-right py-1.5">Users</th>
                <th className="text-right py-1.5">Fails</th>
                <th className="text-left py-1.5">Sev</th>
              </tr></thead>
              <tbody data-testid="cred-stuffing-rows">
                {data.cred_stuffing.map(r => (
                  <tr key={r.ip} className="border-t border-slate-100">
                    <td className="py-1.5 font-mono text-[11px]">{r.ip}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.distinct_users}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.total_failures}</td>
                    <td className="py-1.5"><Badge variant="outline" className={`${SEVERITY_CLS[r.severity]} text-[10px]`}>{r.severity}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-rose-500" /> Password spray</h4>
          <p className="text-[10.5px] text-slate-500 mb-2">20+ failures from a single IP</p>
          {data.password_spray.length === 0 ? (
            <div className="text-[11.5px] text-slate-400 italic">None detected.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10.5px] uppercase text-slate-500"><tr>
                <th className="text-left py-1.5">IP</th>
                <th className="text-right py-1.5">Fails</th>
                <th className="text-right py-1.5">Users</th>
                <th className="text-left py-1.5">Sev</th>
              </tr></thead>
              <tbody data-testid="password-spray-rows">
                {data.password_spray.map(r => (
                  <tr key={r.ip} className="border-t border-slate-100">
                    <td className="py-1.5 font-mono text-[11px]">{r.ip}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.total_failures}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.distinct_users}</td>
                    <td className="py-1.5"><Badge variant="outline" className={`${SEVERITY_CLS[r.severity]} text-[10px]`}>{r.severity}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p className="text-[10.5px] text-slate-500 italic">{data._methodology}</p>
    </div>
  );
}

function ExaminerPackTab() {
  const [name, setName] = useState("");
  const [org, setOrg] = useState("OCC");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetchPack = async () => {
    if (!name) return;
    setBusy(true);
    try {
      const r = await api.get(`/partner/banking/ops-defense/examiner-pack?examiner_name=${encodeURIComponent(name)}&examiner_org=${encodeURIComponent(org)}`);
      setData(r.data);
    } catch {
      toast.error("Could not generate pack");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="examiner-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-semibold mb-3">Generate examiner-aligned bundle</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Examiner name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" className="mt-1.5 text-sm" data-testid="examiner-name" />
          </div>
          <div>
            <Label className="text-xs">Org</Label>
            <Select value={org} onValueChange={setOrg}>
              <SelectTrigger className="mt-1.5 h-9 text-xs" data-testid="examiner-org"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="OCC">OCC</SelectItem>
                <SelectItem value="FDIC">FDIC</SelectItem>
                <SelectItem value="FRB">Federal Reserve</SelectItem>
                <SelectItem value="NCUA">NCUA</SelectItem>
                <SelectItem value="State">State examiner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={fetchPack} disabled={!name || busy} className="w-full" data-testid="examiner-gen-btn">
              {busy ? "Generating…" : "Generate manifest"}
            </Button>
          </div>
        </div>
      </div>

      {data && (
        <div className="space-y-3">
          <div className="rounded-xl border-2 border-sky-200 bg-sky-50 p-4">
            <div className="text-sm text-sky-800">
              <strong>{data.examiner_name}</strong> · {data.examiner_org} · <strong>{fmt(data.ffiec_request_count)}</strong> standard items
            </div>
            <Link to={data.fulfillment_link} className="text-[11.5px] text-sky-600 hover:underline mt-1 inline-flex items-center gap-0.5">
              Open diligence packet ZIP exporter <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.coverage.map(c => (
              <div key={c.section} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{c.section}</div>
                <div className="text-base font-semibold tabular-nums mt-0.5">{c.items} items</div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">{c.evidence_source}</div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">#</th>
                  <th className="text-left px-3 py-2 font-medium">Section</th>
                  <th className="text-left px-3 py-2 font-medium">Item</th>
                </tr>
              </thead>
              <tbody data-testid="examiner-manifest-rows">
                {data.manifest.map(m => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-[11px] font-mono text-slate-500">{m.id}</td>
                    <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px]">{m.section}</Badge></td>
                    <td className="px-3 py-1.5 text-[12.5px] text-slate-700">{m.item}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10.5px] text-slate-500 italic">{data._note}</p>
        </div>
      )}
    </div>
  );
}

export function BankingOpsDefense() {
  return (
    <div className="space-y-5" data-testid="banking-ops-defense">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-slate-500" /> Operational Defense
        </h1>
        <p className="text-[13px] text-slate-500 mt-1">
          Insider-threat surveillance · daily reconciliation variance · vendor SLA tracking · account-takeover defense · examiner-pack generator. The operational backbone banks expect.
        </p>
      </div>
      <Tabs defaultValue="insider" className="w-full">
        <TabsList data-testid="ops-tabs" className="flex-wrap">
          <TabsTrigger value="insider"  data-testid="tab-insider">Insider Threat</TabsTrigger>
          <TabsTrigger value="recon"    data-testid="tab-recon">Recon Variance</TabsTrigger>
          <TabsTrigger value="vendor"   data-testid="tab-vendor">Vendor SLA</TabsTrigger>
          <TabsTrigger value="ato"      data-testid="tab-ato">ATO Defense</TabsTrigger>
          <TabsTrigger value="examiner" data-testid="tab-examiner">Examiner Pack</TabsTrigger>
        </TabsList>
        <TabsContent value="insider"  className="mt-4"><InsiderThreatTab /></TabsContent>
        <TabsContent value="recon"    className="mt-4"><ReconVarianceTab /></TabsContent>
        <TabsContent value="vendor"   className="mt-4"><VendorSlaTab /></TabsContent>
        <TabsContent value="ato"      className="mt-4"><AtoTab /></TabsContent>
        <TabsContent value="examiner" className="mt-4"><ExaminerPackTab /></TabsContent>
      </Tabs>
    </div>
  );
}
